import React, { useState, useEffect } from 'react';
import GetRequest from 'components/common/GetRequest.jsx';
import QuoteBuilderForm from 'components/layout_and_quote_builder/quote_builder/QuoteBuilderForm.jsx';

// The İç sıvama (Inside lamination) tab body:
//   1. the single material dimension it inherits from the hard_cardboard section it follows
//      (Frank::InsideLaminationMaterialDimension),
//   2. the section's own nav_section form (its wastage + printing question),
//   3. the material (Malzeme) price -- shown only once there is a material dimension to bill against, since
//      inside lamination is priced on that inherited board.
// It re-fetches the dimension when the hard_cardboard solution changes (navSectionsUpdated) or is discarded
// (navSectionsReset). No SVG here, like the Profit & Additional Costs section.
const InsideLaminationBody = ({ config, navSection, userQuoteId, sectionIndex, unit }) => {
  const [materialDimension, setMaterialDimension] = useState(null);

  useEffect(() => {
    if (userQuoteId == null) { return undefined; }

    const fetchDimension = () => {
      GetRequest({
        url: `/mcc/${userQuoteId}/inside-lamination-material-dimension/${sectionIndex}?unit=${unit}`,
        callback: (data) => {
          setMaterialDimension(data && data.layout_dimension_id ? {
            label: data.material_dimension,
            layoutDimensionId: data.layout_dimension_id,
            width: data.layout_dimension_width,
            height: data.layout_dimension_height,
            printingMethod: data.printing_method,
            acceptedPrintingTypes: data.printing_machine_accepted_printing_types,
          } : null);
        },
      });
    };
    fetchDimension();
    window.addEventListener('navSectionsUpdated', fetchDimension);
    window.addEventListener('navSectionsReset', fetchDimension);
    return () => {
      window.removeEventListener('navSectionsUpdated', fetchDimension);
      window.removeEventListener('navSectionsReset', fetchDimension);
    };
  }, [userQuoteId, sectionIndex, unit]);

  // The material sheet the price is billed against -- the inherited board. Margins are 0 (the board is the whole
  // sheet) and num_total / num_of_drawings_in_set are placeholders: the phase-4 calculator uses a fixed sheet
  // count, so they do not affect the price (phase 5 computes the real sheet count).
  const materialOptions = materialDimension && {
    layout: {
      layout_dimension_id: materialDimension.layoutDimensionId,
      layout_dimension_width: materialDimension.width,
      layout_dimension_height: materialDimension.height,
      layout_dimension_unit: 'mm',
      layout_dimension_bottom_margin: 0,
      layout_dimension_side_margin: 0,
      layout_dimension_top_margin: 0,
      num_total: 1,
      num_of_drawings_in_set: 1,
    },
  };

  // The printing item bills the SAME inherited board, so it reuses the material layout above. It also needs the
  // print size flat (the machine filters read options.layout_dimension_width/height as numbers) and the offset
  // printing method + its accepted printing types (from the endpoint) to filter the printing machines.
  const printingOptions = materialDimension && {
    ...materialOptions,
    layout_dimension_width: parseFloat(materialDimension.width),
    layout_dimension_height: parseFloat(materialDimension.height),
    printing_method: materialDimension.printingMethod,
    printing_machine_accepted_printing_types: materialDimension.acceptedPrintingTypes,
  };

  // The lamination (Sıvama) item bills the SAME inherited board. It needs the print size flat (the machine/labor
  // filters read options.layout_dimension_width/height as numbers) and the material_attribute of the İç sıvama
  // material -- a coating -- so its machine/labor list filters and validates against that coating, as the general
  // lamination section does for a cover coating.
  const laminationOptions = materialDimension && {
    ...materialOptions,
    layout_dimension_width: parseFloat(materialDimension.width),
    layout_dimension_height: parseFloat(materialDimension.height),
    material_attribute: 'coating_material_user_price_id',
  };

  return (
    <div data-frank-inside-lamination-body>
      {materialDimension && materialDimension.label && (
        <div style={{ textAlign: 'center', fontSize: '13px', margin: '28px 0 0' }}>
          <span style={{ color: '#888' }}>{t('quote_builder.material_dimensions')}:</span>{' '}
          <strong style={{ color: '#333' }}>{materialDimension.label}</strong>
        </div>
      )}

      {/* The section's own nav_section form -- its wastage and printing question. Rendered on its own (not through
          QuoteBuilderNavSection, which would also pull pricing sub-sections this pricing-only section has none of):
          a memberless synthetic item keeps QuoteBuilderForm happy, and getExtraItems is a no-op because inside
          lamination has no lamination field to fetch window/cellophane extras for. */}
      <div className="quote_builder_container">
        <QuoteBuilderForm
          config={config.nav_section}
          navSection={navSection}
          item={{ uuid: `inside_lamination_${sectionIndex}` }}
          options={{}}
          getExtraItems={() => {}}
          readOnly={window.user_quote_finalised}
        />
      </div>

      {/* The material (Malzeme) price -- only once a material dimension exists to bill against (the tab itself is
          gated on the hard_cardboard solution). Keyed by the dimension so a re-solved hard_cardboard re-seeds and
          re-prices it. Its price persists to this nav section and flows into the quote total.
          columnTitle is "" (this section has no columns -- UserQuoteNavColumn#secondary_identifier): the saved
          selection caches under `${section title}_${columnTitle}`, and getInitialValueFromCache bails when
          columnTitle is undefined, so passing the empty string is what lets the price restore on reload. */}
      {materialDimension && (
        <div className="quote_builder_container">
          <QuoteBuilderForm
            key={materialDimension.layoutDimensionId}
            config={config.inside_lamination_material}
            navSection={navSection}
            item={navSection.drawing}
            section_uuid={`inside_lamination_material_${sectionIndex}`}
            options={materialOptions}
            columnTitle=""
            getExtraItems={() => {}}
            readOnly={window.user_quote_finalised}
          />
        </div>
      )}

      {/* The printing (Baskı) price -- the colour, a printing machine (filtered by the inherited board's print
          size), and the extra colours. Shown alongside the material price once a material dimension exists, keyed
          by it so a re-solved hard_cardboard re-seeds and re-prices it. Its fire adeti is the section's own (the
          nav_section wastage field), and it bills the same board count the material price does. Same drawing item
          and columnTitle="" as the material form, so its price persists and restores on reload. */}
      {materialDimension && (
        <div className="quote_builder_container">
          <QuoteBuilderForm
            key={materialDimension.layoutDimensionId}
            config={config.inside_lamination_printing}
            navSection={navSection}
            item={navSection.drawing}
            section_uuid={`inside_lamination_printing_${sectionIndex}`}
            options={printingOptions}
            columnTitle=""
            getExtraItems={() => {}}
            readOnly={window.user_quote_finalised}
          />
        </div>
      )}

      {/* The lamination (Sıvama) price -- the method (None / Manual / Machine) and a lamination machine or manual
          labor, filtered by the selected İç sıvama material (a coating). Shown alongside the other prices once a
          material dimension exists, keyed by it. Its fire adeti is the section's own, and it bills the same board
          count the material price does. Same drawing item and columnTitle="" so its price persists on reload. */}
      {materialDimension && (
        <div className="quote_builder_container">
          <QuoteBuilderForm
            key={materialDimension.layoutDimensionId}
            config={config.inside_lamination_lamination}
            navSection={navSection}
            item={navSection.drawing}
            section_uuid={`inside_lamination_lamination_${sectionIndex}`}
            options={laminationOptions}
            columnTitle=""
            getExtraItems={() => {}}
            readOnly={window.user_quote_finalised}
          />
        </div>
      )}
    </div>
  );
};

export default InsideLaminationBody;
